/**
 * useUrlFilters — URL is the single source of truth for filter state.
 *
 * No useState for filters. Read from URL on every render.
 * Write to URL via history.push (creates back-button entry) or history.replace (overwrites current).
 *
 * Back button works automatically — it changes the URL, which changes
 * props.location, which re-renders the component with correct values.
 */

type FilterType = 'string' | 'number';

interface FilterDef {
    default: any;
    type: FilterType;
}

type FilterDefs = Record<string, FilterDef>;
type FilterValues<T extends FilterDefs> = { [K in keyof T]: any };

function parseParam(value: string | null, def: FilterDef): any {
    if (value === null || value === undefined || value === '') return def.default;
    if (def.type === 'number') {
        const n = Number(value);
        return isNaN(n) ? def.default : n;
    }
    return value;
}

function buildSearch<T extends FilterDefs>(defs: T, values: Partial<FilterValues<T>>): string {
    const params = new URLSearchParams();
    for (const key of Object.keys(defs)) {
        const value = values[key];
        const def = defs[key];
        if (value !== def.default && value !== null && value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    }
    const s = params.toString();
    return s ? '?' + s : '';
}

export function useUrlFilters<T extends FilterDefs>(
    history: any,
    location: any,
    defs: T
) {
    // Read filters directly from URL — no useState
    const params = new URLSearchParams(location.search);
    const filters: FilterValues<T> = {} as any;
    for (const key of Object.keys(defs)) {
        (filters as any)[key] = parseParam(params.get(key), defs[key]);
    }

    // push = new history entry (back button returns to previous state)
    function setFilter(key: keyof T, value: any) {
        const next = { ...filters, [key]: value };
        history.push({ pathname: location.pathname, search: buildSearch(defs, next) });
    }

    function setFilters(updates: Partial<FilterValues<T>>) {
        const next = { ...filters, ...updates };
        history.push({ pathname: location.pathname, search: buildSearch(defs, next) });
    }

    // replace = overwrite current entry (for search typing, no history pollution)
    function replaceFilter(key: keyof T, value: any) {
        const next = { ...filters, [key]: value };
        history.replace({ pathname: location.pathname, search: buildSearch(defs, next) });
    }

    function replaceFilters(updates: Partial<FilterValues<T>>) {
        const next = { ...filters, ...updates };
        history.replace({ pathname: location.pathname, search: buildSearch(defs, next) });
    }

    return { filters, setFilter, setFilters, replaceFilter, replaceFilters };
}
